// The task's conversation and activity (spec 06 "the thread in the side panel; activity (from
// events) under a tab"; Work OS comm-rail.tsx): Conversation is the task's thread (`tasks.thread`
// then `messages.list`, posted with `messages.create` under a `client_id`), Activity is the task's
// events (`tasks.history`) as sentences. Threads are `chat` routes: with the add-on off the tab
// says so. wp/ACTIONS: each message pins, edits and deletes like a chat one (task-comment-actions).
import { newIdempotencyKey } from "@companyos/sdk";
import {
  Attachment,
  AttachmentAction,
  AttachmentActions,
  AttachmentContent,
  AttachmentDescription,
  AttachmentGroup,
  AttachmentTitle,
  Button,
  type ChatMessage,
  Composer,
  cn,
  EmptyState,
  Input,
  type MentionItem,
  MessageList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  toast,
  ViewTabs,
} from "@companyos/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  HistoryIcon,
  MessageSquareIcon,
  MessagesSquareIcon,
  SlidersHorizontalIcon,
  Undo2Icon,
  XIcon,
} from "lucide-react";
import { type ReactNode, useRef, useState } from "react";
import type { Api } from "../../api/client";
import { useOptimisticMutation } from "../../api/optimistic";
import type { MeResponse } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { localMessage } from "../chat/chat-optimistic";
import type { CompanyAccess } from "../company";
import { problemText } from "../settings/problem-text";
import { useFeature } from "../use-feature";
import { fileKeys, formatBytes } from "./files";
import { PRIORITIES, type SeatPeople } from "./model";
import { patchThreadMessage } from "./task-cache";
import { useCommentActions } from "./task-comment-actions";
import type { Board, FieldDef } from "./view-engine";
import { applyUndo } from "./with-undo";
import { historyQuery, invalidateWork, threadQuery, workKeys } from "./work-queries";

type MessageCreate = Parameters<Api["client"]["messages"]["create"]>[0]["body"];

type Event = Awaited<ReturnType<Api["client"]["tasks"]["history"]>>["data"][number];

type DescribeContext = {
  people: Pick<SeatPeople, "of" | "nameOf">;
  board: Pick<Board, "status_set"> | undefined;
  fields: readonly Pick<FieldDef, "id" | "name" | "options">[];
  boardName: (id: string) => string;
};

/** A long text cut for one activity line. */
const clip = (t: string, n = 160) => (t.length > n ? `${t.slice(0, n - 1)}…` : t);

const day = (iso: unknown) =>
  typeof iso === "string"
    ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" })
    : "no date";

type FiringRule = NonNullable<
  Awaited<ReturnType<Api["client"]["tasks"]["history"]>>["rules"]
>[string];

/**
 * B1-DE-RULES: the rule behind a system event, from the history's `rules` (keyed by the firing,
 * the event's `correlation_id`); undefined for any other event.
 */
export function ruleOf(
  e: Pick<Event, "actor" | "correlation_id">,
  rules: Readonly<Record<string, FiringRule>> | undefined,
): FiringRule | undefined {
  return e.actor.kind === "system" && e.correlation_id ? rules?.[e.correlation_id] : undefined;
}

/** Who did it: a member's name, an agency operator, or CompanyOS itself (a rule: whom it ran as). */
export function actorName(
  e: Pick<Event, "actor">,
  people: Pick<SeatPeople, "nameOf">,
  rule?: FiringRule,
): string {
  if (rule) return rule.by_name ?? "CompanyOS";
  if (e.actor.kind === "system") return "CompanyOS";
  if (e.actor.kind === "agency_user") return "An agency operator";
  return people.nameOf(e.actor.membership_id ?? null).name;
}

/** One event as a sentence (without the actor): "moved it from Screening to Interview". */
/**
 * DECISION-LEDGER: the Activity row's Undo — an optional reason, then Undo. Every undo of an
 * agent's write is logged on the task's Activity and the agent's record, with who and why.
 */
function UndoWithReason({ onUndo }: { onUndo: (reason: string) => Promise<unknown> }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = () => {
    setBusy(true);
    void onUndo(reason).finally(() => {
      setBusy(false);
      setOpen(false);
      setReason("");
    });
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <button
            type="button"
            aria-label="Undo this change"
            title="Undo (within 15 minutes)"
            className="shrink-0 self-center rounded p-0.5 text-muted-foreground opacity-60 hover:bg-muted hover:text-foreground hover:opacity-100 focus-visible:opacity-100 group-hover:opacity-100"
          />
        }
      >
        <Undo2Icon aria-hidden className="size-3.5" />
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-72 flex-col gap-2 p-3" aria-label="Undo">
        <Input
          aria-label="Why (optional)"
          placeholder="Why (optional)"
          maxLength={500}
          value={reason}
          onChange={(ev) => setReason(ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === "Enter") submit();
          }}
        />
        <Button size="sm" disabled={busy} onClick={submit}>
          Undo
        </Button>
      </PopoverContent>
    </Popover>
  );
}

export function describeEvent(e: Pick<Event, "type" | "payload">, ctx: DescribeContext): string {
  const p = e.payload as Record<string, unknown>;
  const status = (k: unknown) => ctx.board?.status_set.find((s) => s.key === k)?.name ?? String(k);
  const seat = (id: unknown) => ctx.people.of(String(id)).label;
  switch (e.type) {
    // DECISION-LEDGER: an undo, whose write it was, as supervisor, and why
    case "task.undone": {
      const agent =
        p.undone_actor_kind === "digital_employee" || p.undone_actor_kind === "personal_agent";
      const why = typeof p.reason === "string" && p.reason ? `: ${clip(p.reason)}` : "";
      return `undid ${agent ? "an agent's change" : "a change"}${p.as_supervisor === true ? " as its supervisor" : ""}${why}`;
    }
    case "task.created":
      return `created the task in ${status(p.status)}`;
    case "task.status_changed":
      return `moved it from ${status(p.from)} to ${status(p.to)}`;
    case "task.assigned":
      return ctx.people.of(String(p.seat_id)).de
        ? `added ${seat(p.seat_id)} as the delegate`
        : `assigned ${seat(p.seat_id)}`;
    case "task.unassigned":
      return `removed ${seat(p.seat_id)}`;
    // HANDOFF-LOG: "Ops Assistant handed this to Researcher: <why>"
    case "task.handed_off": {
      const to = ((p.to_seat_ids as unknown[] | undefined) ?? []).map(seat).join(", ");
      const why = typeof p.reason === "string" && p.reason ? `: ${clip(p.reason)}` : "";
      return `handed this to ${to || "someone"}${why}`;
    }
    case "message.created":
      return `commented: “${clip(String(p.body_text ?? ""))}”`;
    case "task.due_changed":
      return p.to ? `set the due date to ${day(p.to)}` : "cleared the due date";
    case "task.priority_changed":
      return `set the priority to ${PRIORITIES.find((x) => x.value === p.to)?.label ?? String(p.to)}`;
    case "task.updated": {
      const changes = Object.keys((p.changes as object | undefined) ?? {});
      const names: Record<string, string> = {
        title: "the title",
        start_at: "the start date",
        estimate_minutes: "the estimate",
        parent_task_id: "the parent task",
        restricted: "who can see it",
      };
      return `changed ${changes.map((c) => names[c] ?? c).join(", ") || "the task"}`;
    }
    case "task.field_changed": {
      const f = ctx.fields.find((x) => x.id === p.field_def_id);
      const choice = f?.options.choices?.find((c) => c.key === p.to)?.name;
      const to = p.to === null || p.to === undefined ? "nothing" : `“${choice ?? String(p.to)}”`;
      return `set ${f?.name ?? "a field"} to ${to}`;
    }
    case "task.checklist_changed": {
      // v2 (DOCS-KB) names the checklist and adds whole-list ops; v1 events read the same.
      const lists: Record<string, string> = {
        list_added: "added a checklist",
        list_renamed: "renamed a checklist",
        list_removed: "removed a checklist",
      };
      const op = String(p.op);
      if (lists[op]) return lists[op];
      if (op === "reordered")
        return p.item_id ? "moved a checklist item" : "reordered the checklist";
      return op === "added"
        ? "added a checklist item"
        : op === "removed"
          ? "removed a checklist item"
          : p.done
            ? "ticked a checklist item"
            : "edited a checklist item";
    }
    case "task.dependency_changed":
      return p.op === "added"
        ? `added a ${p.kind === "soft" ? "soft" : "blocking"} dependency`
        : "removed a dependency";
    case "task.moved":
      return `moved it to ${ctx.boardName(String(p.to_board_id))}`;
    case "task.deleted":
      return "deleted the task";
    // TASK-FILES
    case "task.file_changed": {
      const name = `“${String(p.file_name)}”`;
      switch (p.op) {
        case "added":
          return p.message_id ? `attached ${name} to a comment` : `attached ${name}`;
        case "renamed":
          return `renamed “${String(p.from_name)}” to ${name}`;
        case "moved":
          return `moved ${name} to ${p.folder_id ? "a folder" : "the top level"}`;
        case "cover_set":
          return `made ${name} a cover`;
        case "cover_cleared":
          return `removed ${name} as a cover`;
        case "trashed":
          return `moved ${name} to the trash`;
        case "restored":
          return `restored ${name}`;
        default:
          return `changed ${name}`;
      }
    }
    case "task.file_folder_changed": {
      const name = `the folder “${String(p.name)}”`;
      if (p.op === "added") return `created ${name}`;
      if (p.op === "renamed") return `renamed ${name}`;
      if (p.op === "moved") return `moved ${name}`;
      const n = Number(p.file_count ?? 0);
      return n
        ? `deleted ${name} (${n} ${n === 1 ? "file" : "files"} to the trash)`
        : `deleted ${name}`;
    }
    case "task.trash_emptied": {
      const n = Number(p.count ?? 0);
      return `emptied the trash (${n} ${n === 1 ? "file" : "files"})`;
    }
    default:
      return e.type.replace(/^task\./, "").replaceAll("_", " ");
  }
}

export type TaskRailTab = "conversation" | "details" | "activity";

/**
 * The task's rail: Conversation, Details (the properties, when the host gives them) and
 * Activity. In the split host (`group/split` with `data-mode=split`) it fills the column: the
 * messages scroll and the composer stays at the bottom. The conversation stays mounted while
 * another tab shows, so an unsent message survives a look at the details.
 */
export function TaskActivity({
  api,
  company,
  taskId,
  ctx,
  details,
  tab: controlled,
  onTabChange,
}: {
  api: Api;
  company: CompanyAccess;
  taskId: string;
  ctx: DescribeContext & { people: SeatPeople };
  /** TASK-DRAWER-CHAT: the properties, as the Details tab. */
  details?: ReactNode;
  tab?: TaskRailTab;
  onTabChange?: (tab: TaskRailTab) => void;
}) {
  const [own, setOwn] = useState<TaskRailTab>("conversation");
  const tab = controlled ?? own;
  const setTab = (t: TaskRailTab) => {
    setOwn(t);
    onTabChange?.(t);
  };
  const fill = "group-data-[mode=split]/split:min-h-0 group-data-[mode=split]/split:flex-1";
  return (
    <section
      aria-label="Conversation and activity"
      className={cn("@container/rail mt-8 flex flex-col group-data-[mode=split]/split:mt-0", fill)}
    >
      <ViewTabs
        aria-label="Conversation and activity"
        // A narrow column drops the tab icons rather than scroll the labels.
        className="shrink-0 group-data-[mode=split]/split:pr-9 @max-[22rem]/rail:[&_svg]:hidden"
        value={tab}
        onValueChange={(v) => setTab(v as TaskRailTab)}
        tabs={[
          { value: "conversation", label: "Conversation", icon: MessageSquareIcon },
          ...(details ? [{ value: "details", label: "Details", icon: SlidersHorizontalIcon }] : []),
          { value: "activity", label: "Activity", icon: HistoryIcon },
        ]}
      />
      <div
        data-slot="task-rail-conversation"
        hidden={tab !== "conversation"}
        className={cn("pt-3", tab === "conversation" && cn("flex flex-col", fill))}
      >
        <Conversation api={api} company={company} taskId={taskId} people={ctx.people} />
      </div>
      {details ? (
        <div
          data-slot="task-rail-details"
          hidden={tab !== "details"}
          className={cn(
            "pt-3",
            tab === "details" && "group-data-[mode=split]/split:overflow-y-auto",
            tab === "details" && fill,
          )}
        >
          {details}
        </div>
      ) : null}
      {tab === "activity" ? (
        <div className={cn("pt-3 group-data-[mode=split]/split:overflow-y-auto", fill)}>
          <Activity api={api} company={company} taskId={taskId} ctx={ctx} />
        </div>
      ) : null}
    </section>
  );
}

function Activity({
  api,
  company,
  taskId,
  ctx,
}: {
  api: Api;
  company: CompanyAccess;
  taskId: string;
  ctx: DescribeContext;
}) {
  const history = useQuery(historyQuery(api, company.id, taskId));
  // AGENT-GUARDRAILS: the writes this person may still undo (an agent's, for 15 minutes), on the
  // Activity row of the write's event.
  const undoable = useQuery({
    queryKey: [...workKeys.history(company.id, taskId), "undo"],
    queryFn: () =>
      api.client.undo.list({
        params: { co: company.id },
        query: { subject_kind: "task", subject_id: taskId },
      }),
  });
  const queryClient = useQueryClient();
  const undoOf = new Map((undoable.data?.items ?? []).map((u) => [u.event_id, u.undo_id]));
  const undo = (undoId: string, reason?: string) =>
    applyUndo(api, company.id, undoId, reason).then(
      () => {
        // the task, its board's cards and every list it shows in
        void invalidateWork(queryClient, company.id);
        toast.add({ title: "Undone", type: "success" });
      },
      (e: unknown) =>
        toast.add({
          title: "Not undone",
          description: problemText(e, "This change could not be undone"),
          type: "error",
        }),
    );
  if (history.isPending)
    return (
      <div role="status" aria-label="Loading the activity" className="flex flex-col gap-2">
        <Skeleton className="h-5" />
        <Skeleton className="h-5" />
      </div>
    );
  const rows = history.data?.data ?? [];
  const rules = history.data?.rules;
  if (!rows.length) return <p className="text-sm text-muted-foreground">No activity yet.</p>;
  return (
    <ol aria-label="Activity" className="m-0 flex list-none flex-col gap-2 p-0">
      {rows.map((e) => {
        const rule = ruleOf(e, rules);
        return (
          <li key={e.id} className="group flex items-baseline gap-2 text-sm">
            <span className="min-w-0 flex-1">
              <span className="font-medium text-foreground">{actorName(e, ctx.people, rule)}</span>
              {rule?.by_de ? (
                <span className="ml-1 rounded bg-muted px-1 align-middle text-[10px] font-medium uppercase text-muted-foreground">
                  DE
                </span>
              ) : e.actor.kind === "digital_employee" ||
                e.actor.kind === "personal_agent" ||
                e.actor.run_id ? (
                <span className="ml-1 rounded bg-muted px-1 align-middle text-[10px] font-medium uppercase text-muted-foreground">
                  agent
                </span>
              ) : null}
              {rule ? <span className="text-muted-foreground"> (rule: {rule.name})</span> : null}{" "}
              <span className="text-secondary">{describeEvent(e, ctx)}</span>
              {e.actor.run_id ? (
                <>
                  {" · "}
                  <Link
                    to="/c/$slug/agents/runs/$id"
                    params={{ slug: company.slug, id: e.actor.run_id }}
                    search={keepFocus}
                    className="text-xs text-primary-text underline-offset-4 hover:underline"
                  >
                    run
                  </Link>
                </>
              ) : null}
            </span>
            {undoOf.has(e.id) ? (
              <UndoWithReason onUndo={(reason) => undo(undoOf.get(e.id) as string, reason)} />
            ) : null}
            <time
              dateTime={e.occurred_at}
              className="shrink-0 text-xs text-muted-foreground tabular-nums"
            >
              {new Date(e.occurred_at).toLocaleString(undefined, {
                day: "numeric",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </time>
          </li>
        );
      })}
    </ol>
  );
}

function Conversation({
  api,
  company,
  taskId,
  people,
}: {
  api: Api;
  company: CompanyAccess;
  taskId: string;
  people: SeatPeople;
}) {
  const chat = useFeature("chat");
  const queryClient = useQueryClient();
  const thread = useQuery({ ...threadQuery(api, company.id, taskId), enabled: chat.enabled });
  // WORK-PERF: the comment is in the conversation before the server answers (CHAT-PERF's local
  // message); the answer replaces it by its client id, a refusal takes it out with a toast.
  const co = company.id;
  const threadKey = workKeys.thread(co, taskId);
  const post = useOptimisticMutation({
    keys: () => [threadKey],
    apply: (v: { markdown: string; mentions: string[]; request: MessageCreate; files: File[] }) => {
      const threadId = thread.data?.thread_id;
      if (!threadId) return;
      const me = queryClient.getQueryData<MeResponse>(["me"]);
      const meId = me?.memberships.find((m) => m.company_id === co)?.membership_id ?? null;
      patchThreadMessage(queryClient, co, localMessage({ co, meId }, threadId, v.request));
    },
    mutationFn: async (v) => {
      const m = await api.client.messages.create({
        params: { co, id: thread.data?.thread_id ?? "" },
        body: v.request,
      });
      // TASK-FILES: the comment's files ride on it (`message_id`); they list under the task too.
      for (const f of v.files) {
        const form = new FormData();
        form.set("file", f, f.name);
        form.set("message_id", m.id);
        await api.client.files.upload({ params: { co }, body: form });
      }
      if (v.files.length)
        void queryClient.invalidateQueries({ queryKey: fileKeys.task(co, taskId) });
      return m;
    },
    reconcile: (m) => patchThreadMessage(queryClient, co, m),
  });
  const [pending, setPending] = useState<File[]>([]);
  const picker = useRef<HTMLInputElement>(null);
  const me = queryClient.getQueryData<MeResponse>(["me"]);
  const meId = me?.memberships.find((m) => m.company_id === co)?.membership_id ?? null;
  const comment = useCommentActions(api, co, taskId, {
    rows: thread.data?.messages ?? [],
    meId,
  });
  const files = useQuery({
    queryKey: fileKeys.live(co, taskId),
    queryFn: () =>
      api.client.files.list({ params: { co }, query: { task_id: taskId, limit: 200 } }),
    enabled: chat.enabled,
  });
  const send = (markdown: string, mentions: string[]) =>
    post.mutate({
      markdown,
      mentions,
      files: pending,
      request: {
        client_id: newIdempotencyKey(),
        body_markdown: markdown,
        ...(mentions.length ? { mentions } : {}),
      } as MessageCreate,
    });
  if (!chat.enabled)
    return (
      <EmptyState
        variant="compact"
        icon={MessagesSquareIcon}
        title="Task threads need Chat"
        description="A task's conversation is a chat thread; it opens here once the Chat add-on is on for this company."
      />
    );
  if (thread.isPending)
    return (
      <div role="status" aria-label="Loading the conversation" className="flex flex-col gap-2">
        <Skeleton className="h-10" />
        <Skeleton className="h-10" />
      </div>
    );
  if (thread.isError)
    return <p className="text-sm text-muted-foreground">The conversation could not be loaded.</p>;
  const mentionables: MentionItem[] = [
    ...people.all
      .filter((p) => p.de)
      .map((p) => ({ id: `seat:${p.seatId}`, name: p.name, kind: "agent" as const, hint: p.hint })),
    ...people.all
      .filter((p) => !p.de && !p.closed)
      .map((p) => ({
        id: `seat:${p.seatId}`,
        name: p.name,
        kind: "person" as const,
        hint: "Seat",
      })),
  ];
  const attached = new Map<string, NonNullable<ChatMessage["attachments"]>>();
  for (const f of files.data?.data ?? [])
    if (f.message_id)
      attached.set(f.message_id, [
        ...(attached.get(f.message_id) ?? []),
        { id: f.id, name: f.file_name, description: formatBytes(f.bytes) },
      ]);
  const messages: ChatMessage[] = (thread.data?.messages ?? []).map((m) => {
    const who = people.nameOf(m.author_membership_id);
    const att = attached.get(m.id);
    return {
      id: m.id,
      author: { id: m.author_membership_id ?? "system", name: who.name, kind: who.kind },
      createdAt: m.created_at,
      body: m.deleted_at ? "This message was deleted." : m.body_text,
      ...(m.edited_at ? { edited: true } : {}),
      ...(m.pinned_at ? { pinned: true } : {}),
      ...(att?.length ? { attachments: att } : {}),
      editable: comment.editable(m),
    };
  });
  return (
    <div className="flex flex-col gap-3 group-data-[mode=split]/split:min-h-0 group-data-[mode=split]/split:flex-1">
      <MessageList
        label="Task conversation"
        messages={messages}
        {...comment.listProps}
        // Stacked: a capped box under the work. Split: the column's height, the composer below.
        className="max-h-[420px] group-data-[mode=split]/split:max-h-none"
        empty={
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <MessagesSquareIcon aria-hidden="true" className="size-4 shrink-0 text-faint" />
            No messages yet: people and digital employees talk about this task here.
          </p>
        }
      />
      <Composer
        className="shrink-0"
        label="Message about this task"
        placeholder="Write a message… Type @ to mention"
        mentionables={mentionables}
        onAttach={() => picker.current?.click()}
        attachments={
          pending.length ? (
            <AttachmentGroup data-slot="comment-files">
              {pending.map((f, i) => (
                <Attachment key={`${f.name}-${f.size}-${f.lastModified}`} size="xs" state="idle">
                  <AttachmentContent>
                    <AttachmentTitle>{f.name}</AttachmentTitle>
                    <AttachmentDescription>{formatBytes(f.size)}</AttachmentDescription>
                  </AttachmentContent>
                  <AttachmentActions>
                    <AttachmentAction
                      aria-label={`Remove ${f.name}`}
                      onClick={() => setPending((p) => p.filter((_, j) => j !== i))}
                    >
                      <XIcon aria-hidden="true" />
                    </AttachmentAction>
                  </AttachmentActions>
                </Attachment>
              ))}
            </AttachmentGroup>
          ) : undefined
        }
        onSubmit={(v) => {
          const text = v.markdown.trim();
          if (!text && !pending.length) return;
          send(text || `Attached ${pending.map((f) => f.name).join(", ")}`, v.mentions);
          setPending([]);
        }}
      />
      <input
        ref={picker}
        type="file"
        multiple
        hidden
        aria-label="Attach files to the message"
        data-slot="comment-file-input"
        onChange={(e) => {
          const chosen = [...(e.target.files ?? [])];
          e.target.value = "";
          if (chosen.length) setPending((p) => [...p, ...chosen]);
        }}
      />
      {comment.dialogs}
    </div>
  );
}
